<script lang="ts">
  import { doc, setDoc } from 'firebase/firestore'
  import { shareReplay, withLatestFrom } from 'rxjs/operators'
  import { combineLatest, fromEvent } from 'rxjs'
  import { onMount } from 'svelte'
  import type { Observable } from 'rxjs'
  import { state, set_state, return_to_lobby } from '../State'
  import { card_count } from '../../Cards/observables'
  import { current_votes, reset_votes } from './Votes'
  import { delete_last_result, results_collection } from '../../Review/Results'
  import type { Card } from '../../shared/codecs/Card'
  import { ResultVoteT } from '../../shared/codecs/Vote'
  import { ResultT } from '../../shared/codecs/Result'
  import CtaButton from '../../Generic/CtaButton.svelte'
  import { compact } from 'lodash'
  import decodeWith from '../../shared/firebase/decodeHelper'
  import Confirmation from '../../Confirmation/Component.svelte'
  import type { Component, ComponentProps } from '../../utilities/typeHelpers'
  import { map } from 'rxjs/operators'
  import { getContext } from 'svelte'
  import { DateTime } from 'luxon'

  const delete_result_and_return = combineLatest([delete_last_result, reset_votes, set_state, state]).pipe(
    map(([delete_last_result, reset_votes, set_state, state]) => {
      return async (): Promise<void> => {
        await delete_last_result()
        await reset_votes()
        await set_state({ current_card: state.current_card - 1 })
      }
    }),
    shareReplay(1),
  )

  const reset_to_lobby = combineLatest([return_to_lobby, reset_votes]).pipe(
    map(([return_to_lobby, reset_votes]) => {
      return async (): Promise<void> => {
        await return_to_lobby()
        await reset_votes()
      }
    }),
    shareReplay(1),
  )

  const {
    open,
  }: {
    open: (component: Component<Confirmation>, props: ComponentProps<Confirmation>) => void
  } = getContext('simple-modal')

  const showBackConfirmation = () =>
    // TODO this casting is ugly as sin, fix where relevant(search "open(Confirmation as unknown as Component<Confirmation>")
    open(Confirmation as unknown as Component<Confirmation>, {
      action: $delete_result_and_return,
      message: 'Are you sure you want to go back? The current votes will be lost.',
    })

  const showReturnToLobbyConfirmation = () =>
    open(Confirmation as unknown as Component<Confirmation>, {
      action: $reset_to_lobby,
      message: 'Are you sure you want to return to the lobby? All of the current votes will be lost.',
    })

  export let current_card: Observable<Card & { id: string }>
  let nextButton: HTMLButtonElement
  let backButton: HTMLButtonElement
  let returnToLobbyButton: HTMLButtonElement

  onMount(() => {
    const sub = fromEvent(nextButton, 'click')
      .pipe(
        withLatestFrom(
          state,
          card_count,
          current_votes,
          current_card,
          results_collection,
          set_state,
          reset_votes,
        ),
      )
      .subscribe(([_, state, card_count, votes, card, results_collection, set_state, reset_votes]) => {
        void reset_votes()
        const castVotes = compact(votes.map(vote => decodeWith(ResultVoteT, vote)))
        const date = DateTime.utc()

        const cardResult = castVotes.reduce(
          (result, vote) => ({
            ...result,
            [vote.color]: result[vote.color] + 1,
          }),
          {
            card: card.id,
            date,
            red: 0,
            amber: 0,
            green: 0,
          },
        )
        console.log(cardResult)
        void setDoc(
          doc(results_collection, date.toISO()?.toString()),
          ResultT.encode({ ...cardResult, deleted: false }),
        )

        if (state.current_card < card_count) {
          void set_state({
            current_card: state.current_card + 1,
            show: false,
          })
        }
        if (state.current_card === card_count) {
          void set_state({ phase: 'review' })
        }
        return () => sub.unsubscribe()
      })
  })

  function toggle_show_votes() {
    void $set_state({
      show: !$state.show,
    })
  }
</script>

<div id="button-container">
  {#if $state.current_card === 1}
    <CtaButton
      bind:inner={returnToLobbyButton}
      on:click={showReturnToLobbyConfirmation}
      id="return-to-lobby"
      kind="negative"
    >
      Return To Lobby
    </CtaButton>
  {:else}
    <CtaButton bind:inner={backButton} on:click={showBackConfirmation} id="back" kind="negative">
      Go Back
    </CtaButton>
  {/if}
  <CtaButton on:click={toggle_show_votes} id="reveal">
    {$state?.show ? 'Hide Votes' : 'Reveal Votes'}
  </CtaButton>
  <CtaButton bind:inner={nextButton} id="next">Next</CtaButton>
</div>

<style>
  #button-container {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    gap: 1.5em;
    margin: 1em;
  }
</style>
